import React, { useState } from 'react';
import { Video, Calendar, Clock, Users, Plus, Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { toast } from 'react-hot-toast';

interface Meeting {
  id: string;
  title: string;
  date: string;
  time: string;
  duration: string;
  organizer: string;
  attendees: Array<{
    name: string;
    avatar: string;
    role: string;
  }>;
  status: 'scheduled' | 'in-progress' | 'completed';
}

interface MeetingForm {
  title: string;
  date: string;
  time: string;
  duration: string;
  attendees: string[];
}

const initialFormState: MeetingForm = {
  title: '',
  date: '',
  time: '',
  duration: '1',
  attendees: [],
};

interface MeetingCenterProps {
  contractId: string;
}

export function MeetingCenter({ contractId }: MeetingCenterProps) {
  const [meetings, setMeetings] = useState<Meeting[]>([
    {
      id: '1',
      title: 'Contract Review Meeting',
      date: '2024-03-15',
      time: '10:00 AM',
      duration: '1 hour',
      organizer: 'Sarah Johnson',
      attendees: [
        {
          name: 'Sarah Johnson',
          avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson',
          role: 'Contract Officer',
        },
        {
          name: 'Michael Chen',
          avatar: 'https://ui-avatars.com/api/?name=Michael+Chen',
          role: 'Technical Lead',
        },
      ],
      status: 'scheduled',
    },
  ]);

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [form, setForm] = useState<MeetingForm>(initialFormState);
  const [saving, setSaving] = useState(false);

  const handleScheduleMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newMeeting: Meeting = {
        id: crypto.randomUUID(),
        title: form.title,
        date: form.date,
        time: form.time,
        duration: `${form.duration} hour${form.duration === '1' ? '' : 's'}`,
        organizer: 'Demo User',
        attendees: [
          {
            name: 'Demo User',
            avatar: 'https://ui-avatars.com/api/?name=Demo+User',
            role: 'Organizer',
          },
          ...form.attendees.map(attendee => ({
            name: attendee,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(attendee)}`,
            role: 'Participant',
          })),
        ],
        status: 'scheduled',
      };

      setMeetings(prev => [...prev, newMeeting]);
      setShowScheduleModal(false);
      setForm(initialFormState);
      toast.success('Meeting scheduled successfully');
    } catch (error) {
      toast.error('Failed to schedule meeting');
      console.error('Failed to schedule meeting:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900">Virtual Meetings</h2>
        <button
          onClick={() => setShowScheduleModal(true)}
          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <Plus className="mr-2 h-4 w-4" />
          Schedule Meeting
        </button>
      </div>

      <div className="space-y-4">
        {meetings.map((meeting) => (
          <div
            key={meeting.id}
            className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="rounded-lg bg-indigo-50 p-3">
                  <Video className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">{meeting.title}</h3>
                  <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Calendar className="mr-1.5 h-4 w-4" />
                      {meeting.date}
                    </span>
                    <span className="flex items-center">
                      <Clock className="mr-1.5 h-4 w-4" />
                      {meeting.time} ({meeting.duration})
                    </span>
                  </div>
                </div>
              </div>
              <span className="inline-flex rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                {meeting.status}
              </span>
            </div>

            <div className="mt-4">
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Attendees</span>
              </div>
              <div className="mt-2 flex -space-x-2">
                {meeting.attendees.map((attendee) => (
                  <img
                    key={attendee.name}
                    src={attendee.avatar}
                    alt={attendee.name}
                    className="h-8 w-8 rounded-full border-2 border-white"
                    title={`${attendee.name} - ${attendee.role}`}
                  />
                ))}
              </div>
            </div>

            <div className="mt-4 flex justify-end space-x-3">
              <button className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                View Details
              </button>
              <button className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                <Video className="mr-2 h-4 w-4" />
                Join Meeting
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Schedule Meeting Modal */}
      <Modal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title="Schedule Meeting"
      >
        <form onSubmit={handleScheduleMeeting} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Meeting Title
            </label>
            <input
              type="text"
              id="title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                Date
              </label>
              <input
                type="date"
                id="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                required
              />
            </div>

            <div>
              <label htmlFor="time" className="block text-sm font-medium text-gray-700">
                Time
              </label>
              <input
                type="time"
                id="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
              Duration
            </label>
            <select
              id="duration"
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="0.5">30 minutes</option>
              <option value="1">1 hour</option>
              <option value="1.5">1.5 hours</option>
              <option value="2">2 hours</option>
            </select>
          </div>

          <div>
            <label htmlFor="attendees" className="block text-sm font-medium text-gray-700">
              Additional Attendees
            </label>
            <select
              id="attendees"
              multiple
              value={form.attendees}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions, option => option.value);
                setForm({ ...form, attendees: values });
              }}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="Sarah Johnson">Sarah Johnson</option>
              <option value="Michael Chen">Michael Chen</option>
              <option value="Emily Rodriguez">Emily Rodriguez</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Hold Ctrl/Cmd to select multiple attendees
            </p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowScheduleModal(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Schedule Meeting
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}